import type { ReactElement } from "react";
import {
  PROBLEM_TREE_CAUSE_STATUSES,
  type ProblemTreeCauseStatus,
} from "@playbookai/widget-schemas";
import { StatusPill } from "../../atoms/index.js";
import {
  Popover,
  PopoverContent,
  PopoverMenuItem,
  PopoverTrigger,
  Tooltip,
} from "../../molecules/index.js";
import type { StatusTone } from "../../lib/tones.js";
import { cn } from "../../lib/utils.js";

interface StatusPresentation {
  readonly label: string;
  /** What the status CLAIMS, so the menu never implies a preference. */
  readonly hint: string;
  readonly tone: Extract<StatusTone, "watch" | "healthy" | "neutral">;
}

/**
 * One registry, not a switch (packages/ui invariant §3). The pill sits on the
 * amber cause sticky, so suspected and confirmed are solid pills rather than
 * soft tints — a soft tint over amber reads as the sticky, not as a status.
 * Ruled out is the neutral pill (neutral never saturates).
 */
export const PROBLEM_TREE_STATUS_PRESENTATION: Readonly<
  Record<ProblemTreeCauseStatus, StatusPresentation>
> = {
  suspected: {
    label: "Suspected",
    hint: "Plausible, evidence is insufficient",
    tone: "watch",
  },
  confirmed: {
    label: "Confirmed",
    hint: "Evidence supports it",
    tone: "healthy",
  },
  ruled_out: {
    label: "Ruled out",
    hint: "Evidence contradicts it",
    tone: "neutral",
  },
};

/**
 * A ruled-out cause is FADED, never removed. The note stays on the tree and in
 * the stored params: the team's reason for discarding it is part of the
 * investigation, and a later contradiction has to be able to find it again.
 */
export const RULED_OUT_FADE = "opacity-45";

/** The status as it reads on a canvas the viewer cannot edit. */
export function CauseStatusBadge({
  status,
  className,
}: {
  readonly status: ProblemTreeCauseStatus;
  /** Layout only (placement, shrink). */
  readonly className?: string | undefined;
}): ReactElement {
  const presentation = PROBLEM_TREE_STATUS_PRESENTATION[status];
  return (
    <StatusPill
      tone={presentation.tone}
      variant="solid"
      size="sm"
      showDot={false}
      className={cn("max-w-full", className)}
      title={presentation.hint}
    >
      {presentation.label}
    </StatusPill>
  );
}

/**
 * The same pill, as the trigger of the shared design-system dropdown.
 *
 * The sticky around it is a draggable ReactFlow node that also opens its text
 * editor on double-click, so the pill has to opt out of both gestures:
 *
 * - `nodrag` / `nopan` are the ONLY thing that stops the drag. xyflow binds
 *   d3-drag natively on the node element, while React delegates at the root,
 *   so a React `stopPropagation` runs long after the drag has already begun.
 * - `stopPropagation` is right for double-click, because the sticky's editor
 *   opens from a React handler on the same root.
 */
export function CauseStatusMenu({
  status,
  causeLabel,
  onChange,
}: {
  readonly status: ProblemTreeCauseStatus;
  readonly causeLabel: string;
  readonly onChange: (next: ProblemTreeCauseStatus) => void;
}): ReactElement {
  const presentation = PROBLEM_TREE_STATUS_PRESENTATION[status];
  return (
    <Popover align="start">
      <Tooltip content={presentation.hint}>
        <PopoverTrigger>
          <StatusPill
            as="button"
            tone={presentation.tone}
            variant="solid"
            size="sm"
            showDot={false}
            aria-label={`Status of ${causeLabel}: ${presentation.label}`}
            onDoubleClick={(event) => event.stopPropagation()}
            className="nodrag nopan max-w-full"
          >
            {presentation.label}
          </StatusPill>
        </PopoverTrigger>
      </Tooltip>
      <PopoverContent className="w-52">
        {PROBLEM_TREE_CAUSE_STATUSES.map((candidate) => (
          <PopoverMenuItem
            key={candidate}
            checked={candidate === status}
            description={PROBLEM_TREE_STATUS_PRESENTATION[candidate].hint}
            onClick={() => onChange(candidate)}
          >
            {PROBLEM_TREE_STATUS_PRESENTATION[candidate].label}
          </PopoverMenuItem>
        ))}
      </PopoverContent>
    </Popover>
  );
}
